import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
// Aliased: the default export would shadow the global `Map` constructor.
import MapGL, { Marker } from "react-map-gl/mapbox";
import type { MapRef } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  Button,
  GradientText,
  Layout,
  SupportIcon,
} from "lola-framework-ui-test";
import { BodyCopy } from "../components/BodyCopy";
import { AuraLayout } from "../components/AuraLayout";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useClaimLink } from "../hooks/useClaimLink";
import { APP_ROUTES } from "../constants";
import { mapboxStyleUrl, mapboxToken } from "../config";
import {
  getFundingStores,
  type IFundingStore,
} from "../services/api";
import {
  DEFAULT_MAP_CENTER,
  isInUnitedStates,
  type GeoPoint,
} from "../utils/geo";
import { getEvents, safeCapture } from "../analytics";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";
import { CloseLight } from "../assets/navIcons";


/** Matches the backend nearby radius (`NEARBY_RADIUS_MILES`) for empty copy. */
const SEARCH_RADIUS_MILES = 40;
const GEOLOCATION_TIMEOUT_MS = 8000;
const MOVE_DEBOUNCE_MS = 400;
const QUERY_PRECISION = 4;
/** Screen-space grid (px) used to collapse markers that would overlap. */
const CLUSTER_CELL_PX = 57;
const CLUSTER_ZOOM_STEP = 2;
const MAX_ZOOM = 17;
const GPS_ZOOM = 12;
/** Miami — first camera and the query origin until the user pans or GPS (US) recenters. */
const DEFAULT_VIEW = {
  latitude: DEFAULT_MAP_CENTER.latitude,
  longitude: DEFAULT_MAP_CENTER.longitude,
  zoom: 11,
};

type LocationStatus =
  | "idle"
  | "requesting"
  | "device"
  | "denied"
  | "unavailable"
  | "timeout"
  | "outside_us";

interface MarkerGroup {
  key: string;
  latitude: number;
  longitude: number;
  stores: IFundingStore[];
}

const queryKeyFor = (lat: number, lng: number): string =>
  `${lat.toFixed(QUERY_PRECISION)},${lng.toFixed(QUERY_PRECISION)}`;

const StoreLogo = ({ store }: { store: IFundingStore }) => {
  const [failed, setFailed] = useState(false);

  if (!store.logoUrl || failed) {
    return (
      <span className="stores-map-logo-fallback" aria-hidden>
        {store.name.slice(0, 1)}
      </span>
    );
  }

  return (
    <img
      src={store.logoUrl}
      alt=""
      className="stores-map-logo"
      onError={() => setFailed(true)}
    />
  );
};

/**
 * Hand the destination to whichever map app the device prefers. Both URLs are
 * https on purpose: the OS routes them to the installed app and a desktop
 * browser still opens the web map instead of an unhandled scheme.
 */
const buildDirectionsUrl = (store: IFundingStore): string => {
  const destination = `${store.address.latitude},${store.address.longitude}`;
  const ua = navigator.userAgent;
  const isApple =
    /iPhone|iPad|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);

  return isApple
    ? `https://maps.apple.com/?daddr=${destination}&q=${encodeURIComponent(store.name)}`
    : `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
};

const openDirections = (store: IFundingStore) => {
  window.open(buildDirectionsUrl(store), "_blank", "noopener,noreferrer");
};

/**
 * Live store locator for Lola cash-deposit. Same chrome as the screenbook
 * gallery map; store rows come from
 * `GET /widget/operations/funding-locations/stores` using the camera center.
 * GPS only recenters the map when the point is in the United States.
 */
export const StoresMapPage = memo(() => {
  useClaimLink();
  const { t } = useTranslation();
  const { theme } = useStore();
  const { redirectTo } = useRouteWithToken();
  const [searchParams] = useSearchParams();
  const operationId = searchParams.get("operationId");
  const partnerColors = useSessionStore(
    (s) =>
      s.session.data?.partner?.theme?.colors as
        | Record<string, string>
        | undefined,
  );

  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    textColorBtn,
    bodyCopy,
    footerColor,
    dropzoneColors,
  } = colorConfig;
  const inactiveColor = partnerColors?.inactived ?? footerColor;
  const accentSolid = partnerColors?.primaryGradient ?? bodyCopy;

  const howToRoute = operationId
    ? `${APP_ROUTES.HOW_TO_DEPOSIT_CASH_PAGE}?operationId=${operationId}`
    : APP_ROUTES.HOW_TO_DEPOSIT_CASH_PAGE;

  const mapRef = useRef<MapRef | null>(null);
  const cardRef = useRef<HTMLElement | null>(null);
  const cardHeightRef = useRef(0);
  const askedForLocation = useRef(false);
  const ignoreMoveUntilRef = useRef(0);
  const debounceRef = useRef<number | null>(null);
  const lastQueryKeyRef = useRef<string | null>(null);
  const fetchGenRef = useRef(0);
  const pendingGpsRef = useRef<GeoPoint | null>(null);

  const [status, setStatus] = useState<LocationStatus>("idle");
  const [origin, setOrigin] = useState<GeoPoint | null>(null);
  const [stores, setStores] = useState<IFundingStore[]>([]);
  const [cameraOutsideUs, setCameraOutsideUs] = useState(false);
  const [storesLoading, setStoresLoading] = useState(true);
  const [storesError, setStoresError] = useState(false);
  const [activeStoreId, setActiveStoreId] = useState<string | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [viewportTick, setViewportTick] = useState(0);
  const [cardHeight, setCardHeight] = useState(0);

  const activeStore = useMemo(
    () => stores.find((store) => store.storeId === activeStoreId) ?? null,
    [stores, activeStoreId],
  );

  const markerGroups = useMemo<MarkerGroup[]>(() => {
    const map = mapLoaded ? mapRef.current?.getMap() : null;
    if (!map) {
      return stores.map((store) => ({
        key: store.storeId,
        latitude: store.address.latitude,
        longitude: store.address.longitude,
        stores: [store],
      }));
    }

    const cells = new Map<string, IFundingStore[]>();
    const singles: MarkerGroup[] = [];
    for (const store of stores) {
      if (store.storeId === activeStoreId) {
        singles.push({
          key: store.storeId,
          latitude: store.address.latitude,
          longitude: store.address.longitude,
          stores: [store],
        });
        continue;
      }
      const point = map.project([
        store.address.longitude,
        store.address.latitude,
      ]);
      const cellKey = `${Math.round(point.x / CLUSTER_CELL_PX)}:${Math.round(point.y / CLUSTER_CELL_PX)}`;
      cells.set(cellKey, [...(cells.get(cellKey) ?? []), store]);
    }

    const grouped = [...cells.entries()].map(([cellKey, cellStores]) => ({
      key: cellStores.length === 1 ? cellStores[0].storeId : `cell:${cellKey}`,
      latitude:
        cellStores.reduce((sum, s) => sum + s.address.latitude, 0) /
        cellStores.length,
      longitude:
        cellStores.reduce((sum, s) => sum + s.address.longitude, 0) /
        cellStores.length,
      stores: cellStores,
    }));

    return [...grouped, ...singles];
  }, [stores, activeStoreId, mapLoaded, viewportTick]);

  const markProgrammaticCamera = useCallback((durationMs: number) => {
    ignoreMoveUntilRef.current = Date.now() + durationMs + 80;
  }, []);

  const fetchStoresAt = useCallback(
    async (lat: number, lng: number, force = false) => {
      const key = queryKeyFor(lat, lng);
      if (!force && key === lastQueryKeyRef.current) return;

      if (!isInUnitedStates(lat, lng)) {
        lastQueryKeyRef.current = key;
        fetchGenRef.current += 1;
        setCameraOutsideUs(true);
        setStores([]);
        setStoresError(false);
        setStoresLoading(false);
        return;
      }

      lastQueryKeyRef.current = key;
      const gen = ++fetchGenRef.current;
      setCameraOutsideUs(false);
      setStoresLoading(true);
      setStoresError(false);
      try {
        const nearby = await getFundingStores({ lat, lng });
        if (gen !== fetchGenRef.current) return;
        setStores(nearby);
      } catch {
        if (gen !== fetchGenRef.current) return;
        setStores([]);
        setStoresError(true);
      } finally {
        if (gen === fetchGenRef.current) setStoresLoading(false);
      }
    },
    [],
  );

  const fetchFromCamera = useCallback(
    (force = false) => {
      const map = mapRef.current;
      if (!map) {
        void fetchStoresAt(
          DEFAULT_VIEW.latitude,
          DEFAULT_VIEW.longitude,
          force,
        );
        return;
      }
      const center = map.getCenter();
      void fetchStoresAt(center.lat, center.lng, force);
    },
    [fetchStoresAt],
  );

  const recenterToGps = useCallback(
    (point: GeoPoint) => {
      const map = mapRef.current;
      if (!map) return;
      markProgrammaticCamera(650);
      map.easeTo({
        center: [point.longitude, point.latitude],
        zoom: Math.max(map.getZoom() ?? GPS_ZOOM, GPS_ZOOM),
        duration: 600,
      });
      void fetchStoresAt(point.latitude, point.longitude);
    },
    [fetchStoresAt, markProgrammaticCamera],
  );

  const requestDeviceLocation = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setStatus("unavailable");
      setOrigin(null);
      pendingGpsRef.current = null;
      return;
    }
    setStatus("requesting");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const point = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        setActiveStoreId(null);
        if (!isInUnitedStates(point.latitude, point.longitude)) {
          pendingGpsRef.current = null;
          setOrigin(null);
          setStatus("outside_us");
          return;
        }
        setOrigin(point);
        setStatus("device");
        pendingGpsRef.current = mapRef.current ? null : point;
        if (mapRef.current) {
          recenterToGps(point);
        }
      },
      (error) => {
        pendingGpsRef.current = null;
        setOrigin(null);
        setStatus(
          error.code === error.PERMISSION_DENIED
            ? "denied"
            : error.code === error.TIMEOUT
              ? "timeout"
              : "unavailable",
        );
      },
      {
        enableHighAccuracy: false,
        timeout: GEOLOCATION_TIMEOUT_MS,
        maximumAge: 300_000,
      },
    );
  }, [recenterToGps]);

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("stores_map"));
  }, []);

  useEffect(() => {
    if (askedForLocation.current) return;
    askedForLocation.current = true;
    requestDeviceLocation();
  }, [requestDeviceLocation]);

  useEffect(() => {
    if (mapboxToken) return;
    void fetchStoresAt(DEFAULT_VIEW.latitude, DEFAULT_VIEW.longitude);
  }, [fetchStoresAt]);

  useEffect(
    () => () => {
      if (debounceRef.current != null) window.clearTimeout(debounceRef.current);
    },
    [],
  );

  const handleMapLoad = useCallback(() => {
    setMapLoaded(true);
    const pending = pendingGpsRef.current;
    pendingGpsRef.current = null;
    if (pending) {
      recenterToGps(pending);
      return;
    }
    markProgrammaticCamera(80);
    fetchFromCamera();
  }, [fetchFromCamera, markProgrammaticCamera, recenterToGps]);

  const handleMoveEnd = useCallback(() => {
    setViewportTick((tick) => tick + 1);
    if (Date.now() < ignoreMoveUntilRef.current) return;
    if (debounceRef.current != null) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      fetchFromCamera();
    }, MOVE_DEBOUNCE_MS);
  }, [fetchFromCamera]);

  const panelOpen = Boolean(activeStore);

  useLayoutEffect(() => {
    const element = cardRef.current;
    if (!element) {
      cardHeightRef.current = 0;
      setCardHeight(0);
      return;
    }
    const apply = () => {
      cardHeightRef.current = element.offsetHeight;
      setCardHeight(element.offsetHeight);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(element);
    return () => observer.disconnect();
  }, [activeStoreId, panelOpen]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || !activeStore) return;
    markProgrammaticCamera(500);
    map.easeTo({
      center: [activeStore.address.longitude, activeStore.address.latitude],
      padding: {
        top: 0,
        left: 0,
        right: 0,
        bottom: cardHeightRef.current + 32,
      },
      duration: 450,
    });
    return () => {
      markProgrammaticCamera(330);
      map.easeTo({
        padding: { top: 0, left: 0, right: 0, bottom: 0 },
        duration: 250,
      });
    };
  }, [activeStore, mapLoaded, markProgrammaticCamera]);

  const formatDistance = (miles?: number) =>
    miles == null ? null : t("storesMap.distance", { miles: miles.toFixed(1) });

  const formatAddress = (store: IFundingStore) =>
    [
      store.address.line1,
      [store.address.city, store.address.state].filter(Boolean).join(", "),
    ]
      .filter(Boolean)
      .join(" · ");

  const themeVars = {
    "--store-accent-btn": backgroundBtn,
    "--store-accent-solid": accentSolid,
    "--store-accent-text": textColorBtn,
    "--store-card-border": inactiveColor,
    "--store-card-surface": background,
    "--store-card-text": bodyCopy,
    "--store-card-muted": footerColor,
  } as React.CSSProperties;

  const statusMessage = storesLoading
    ? t("storesMap.status.loadingStores")
    : storesError
      ? t("storesMap.status.error")
      : cameraOutsideUs
        ? t("storesMap.status.outsideArea")
        : status === "requesting"
          ? t("storesMap.status.requesting")
          : status === "device"
            ? t("storesMap.status.device")
            : status === "outside_us"
              ? t("storesMap.status.outsideDevice")
              : status === "denied"
                ? t("storesMap.status.denied")
                : status === "timeout"
                  ? t("storesMap.status.timeout")
                  : status === "unavailable"
                    ? t("storesMap.status.unavailable")
                    : null;

  const subtitle = storesLoading
    ? t("storesMap.status.loadingStores")
    : stores.length > 0
      ? t("storesMap.subtitle", { count: stores.length })
      : storesError
        ? t("storesMap.status.error")
        : cameraOutsideUs
          ? t("storesMap.status.outsideArea")
          : t("storesMap.subtitleEmpty");

  return (
    <AuraLayout
      colorConfig={colorConfig}
      className="stores-map-page need-help-header"
    >
      <Layout.Header style={themeVars}>
        <Navbar
          title={t("common.needHelp")}
          align="right"
          color={bodyCopy}
          onBackClick={() => redirectTo(howToRoute)}
          onIconClick={() => redirectTo(APP_ROUTES.SUPPORT_PAGE)}
          icon={<SupportIcon size={35} colors={dropzoneColors} />}
        />
      </Layout.Header>

      <Layout.Content className="stores-map-section" style={themeVars}>
        <section className="stores-map-copy">
          <GradientText as="h1" textColor={bodyCopy} className="stores-map-title">
            {t("storesMap.title")}
          </GradientText>
          <BodyCopy textColor={bodyCopy} className="stores-map-subtitle">
            {subtitle}
          </BodyCopy>
        </section>

        <div
          className={`stores-map-canvas${panelOpen ? " stores-map-canvas--panel-open" : ""}`}
          style={
            { "--stores-map-panel-h": `${cardHeight}px` } as React.CSSProperties
          }
          aria-busy={storesLoading}
        >
          {mapboxToken ? (
            <MapGL
              ref={mapRef}
              mapboxAccessToken={mapboxToken}
              mapStyle={mapboxStyleUrl}
              initialViewState={DEFAULT_VIEW}
              onLoad={handleMapLoad}
              onMoveEnd={handleMoveEnd}
              reuseMaps
              style={{ width: "100%", height: "100%" }}
            >
              {origin && status === "device" && (
                <Marker
                  latitude={origin.latitude}
                  longitude={origin.longitude}
                  anchor="center"
                >
                  <span
                    className="stores-map-origin stores-map-origin--device"
                    aria-hidden
                  />
                </Marker>
              )}

              {markerGroups.map((group) => {
                const [store] = group.stores;
                const clustered = group.stores.length > 1;
                const selected = !clustered && store.storeId === activeStoreId;
                return (
                  <Marker
                    key={group.key}
                    latitude={group.latitude}
                    longitude={group.longitude}
                    anchor="bottom"
                    style={selected ? { zIndex: 3 } : undefined}
                  >
                    <button
                      type="button"
                      className={`stores-map-marker${selected ? " stores-map-marker--selected" : ""}`}
                      aria-pressed={selected}
                      aria-label={
                        clustered
                          ? t("storesMap.clusterLabel", {
                              count: group.stores.length,
                            })
                          : `${store.name} — ${formatAddress(store)}`
                      }
                      onClick={(event) => {
                        event.stopPropagation();
                        setListOpen(false);
                        if (clustered) {
                          markProgrammaticCamera(500);
                          mapRef.current?.easeTo({
                            center: [group.longitude, group.latitude],
                            zoom: Math.min(
                              (mapRef.current.getZoom() ?? 11) +
                                CLUSTER_ZOOM_STEP,
                              MAX_ZOOM,
                            ),
                            duration: 450,
                          });
                          return;
                        }
                        setActiveStoreId(store.storeId);
                      }}
                    >
                      <span
                        className={`stores-map-marker-inner${clustered ? " stores-map-marker-inner--cluster" : ""}`}
                      >
                        {clustered ? (
                          group.stores.length
                        ) : (
                          <StoreLogo store={store} />
                        )}
                      </span>
                    </button>
                  </Marker>
                );
              })}
            </MapGL>
          ) : (
            <section className="stores-map-setup" role="status">
              <GradientText
                as="h2"
                textColor={bodyCopy}
                className="stores-map-setup-title"
              >
                {t("storesMap.setup.title")}
              </GradientText>
              <BodyCopy textColor={footerColor} className="stores-map-setup-body">
                {t("storesMap.setup.body")}
              </BodyCopy>
              <code className="stores-map-setup-code">MAPBOX_TOKEN</code>
            </section>
          )}

          {statusMessage && !listOpen && (
            <p
              className={`stores-map-status${storesLoading ? " stores-map-status--loading" : ""}`}
              role="status"
              aria-live="polite"
            >
              {statusMessage}
            </p>
          )}

          {!listOpen && (
            <div className="stores-map-actions">
              <button
                type="button"
                className="stores-map-action"
                onClick={requestDeviceLocation}
                disabled={status === "requesting"}
              >
                {t("storesMap.locate")}
              </button>
              <button
                type="button"
                className="stores-map-action"
                aria-expanded={false}
                disabled={stores.length === 0}
                onClick={() => {
                  setActiveStoreId(null);
                  setListOpen(true);
                }}
              >
                {t("storesMap.showList")}
              </button>
            </div>
          )}

          {storesError && !listOpen && (
            <section className="stores-map-empty" role="alert">
              <BodyCopy textColor={bodyCopy} className="stores-map-empty-copy">
                {t("storesMap.error")}
              </BodyCopy>
              <button
                type="button"
                className="stores-map-action stores-map-action--active"
                onClick={() => fetchFromCamera(true)}
              >
                {t("common.retry")}
              </button>
            </section>
          )}

          {!storesError &&
            !storesLoading &&
            stores.length === 0 &&
            !listOpen &&
            !activeStore && (
              <section className="stores-map-empty" role="status">
                <BodyCopy textColor={bodyCopy} className="stores-map-empty-copy">
                  {cameraOutsideUs
                    ? t("storesMap.status.outsideArea")
                    : t("storesMap.empty", { miles: SEARCH_RADIUS_MILES })}
                </BodyCopy>
              </section>
            )}

          {listOpen && stores.length > 0 && (
            <section
              className="stores-map-list"
              aria-label={t("storesMap.listLabel")}
            >
              <ScrollFadeIndicator color={bodyCopy} />
              <header className="stores-map-list-head">
                <span className="stores-map-list-title">
                  {t("storesMap.listLabel")}
                </span>
                <button
                  type="button"
                  className="stores-map-action stores-map-action--active"
                  aria-expanded
                  onClick={() => setListOpen(false)}
                >
                  {t("storesMap.showMap")}
                </button>
              </header>
              <ul className="stores-map-list-items">
                {stores.map((store) => (
                  <li key={store.storeId}>
                    <button
                      type="button"
                      className="stores-map-list-item"
                      onClick={() => {
                        setListOpen(false);
                        setActiveStoreId(store.storeId);
                      }}
                    >
                      <span className="stores-map-list-logo">
                        <StoreLogo store={store} />
                      </span>
                      <span className="stores-map-list-text">
                        <span className="stores-map-list-name">{store.name}</span>
                        <span className="stores-map-list-address">
                          {formatAddress(store)}
                        </span>
                      </span>
                      {formatDistance(store.distanceMiles) && (
                        <span className="stores-map-list-distance">
                          {formatDistance(store.distanceMiles)}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {activeStore && (
            <section
              ref={cardRef}
              className="stores-map-card"
              aria-label={activeStore.name}
            >
              <div className="stores-map-card-head">
                <span className="stores-map-card-logo">
                  <StoreLogo store={activeStore} />
                </span>
                <span className="stores-map-card-text">
                  <span className="stores-map-card-name">{activeStore.name}</span>
                  <span className="stores-map-card-address">
                    {formatAddress(activeStore)} {activeStore.address.zip}
                  </span>
                  {formatDistance(activeStore.distanceMiles) && (
                    <span className="stores-map-card-meta">
                      <span className="stores-map-card-distance">
                        {formatDistance(activeStore.distanceMiles)}
                      </span>
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  className="stores-map-card-close"
                  aria-label={t("storesMap.close")}
                  onClick={() => setActiveStoreId(null)}
                >
                  <CloseLight size={31} colors={[bodyCopy, bodyCopy]} />
                </button>
              </div>
              <Button
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                aria-label={t("storesMap.directionsLabel", {
                  name: activeStore.name,
                })}
                onClick={() => openDirections(activeStore)}
              >
                {t("storesMap.directions")}
              </Button>
            </section>
          )}
        </div>
      </Layout.Content>
    </AuraLayout>
  );
});
